import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import BackendService from '../services/backendService';
import { TenantUserRole } from '../services/backendService/backendService';
import useRouterMatch from './useRouterMatch';
export const usersCacheKey = 'workspaceUsers';

function useTenantUsers() {
  const client = useQueryClient();
  const { tenantId } = useRouterMatch();
  const { data, isFetching, error } = useQuery({
    queryKey: [usersCacheKey, tenantId],

    queryFn: () => {
      if (!tenantId) return Promise.resolve(undefined);
      return BackendService.instance.tenant.listUsers(tenantId);
    },

    enabled: Boolean(tenantId),
    staleTime: 1000 * 60 * 15
  });

  const { mutate: editUserProfile, isSuccess: isEditUserProfileSuccess } = useMutation({
    mutationFn: (config: {
      data: { role: TenantUserRole; userId: string };
      onSuccess?: () => void;
    }) => {
      return BackendService.instance.tenant.editUserProfile(config.data);
    },

    onSettled: () => {
      client.invalidateQueries({
        queryKey: [usersCacheKey]
      });
    },

    onSuccess: (data, { onSuccess }) => {
      if (onSuccess) {
        onSuccess();
      }
    }
  });
  const { mutate: removeUser, isPending: isRemovingUser } = useMutation({
    mutationFn: (config: {
      uid: string;
      onSuccess?: () => void;
      onError?: (error: unknown) => void;
    }) => {
      return BackendService.instance.tenant.removeUser(config.uid);
    },

    onSettled: () => {
      client.invalidateQueries({
        queryKey: [usersCacheKey]
      });
    },

    onError: (error, { onError }) => {
      if (onError) {
        onError(error);
      }
    },

    onSuccess: (data, { onSuccess }) => {
      if (onSuccess) {
        onSuccess();
      }
    }
  });
  return {
    users: data?.users,
    usersById: data?.byId || {},
    error,
    removeUser,
    isRemovingUser,
    editUserProfile,
    isEditUserProfileSuccess,
    isLoading: isFetching,
  };
}

export default useTenantUsers;
